import { RepeatClockIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  Heading,
  Image,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import { getRequests, storeRequest } from "../../../services/requestsApi";
import { trackEvent } from "../../../shared/analytics";

export const RequestCard = ({ request }) => (
  <Box bg="gray.700" borderRadius="lg" overflow="hidden">
    <Flex direction="row">
      {request.completed && request.readingOrder ? (
        <Link to={`/browse/${request.readingOrder.id}/preview`}>
          <Image
            src={request.readingOrder.thumbnail}
            alt={request.readingOrder.title}
            maxW="120px"
            height="100%"
            objectFit="cover"
          />
        </Link>
      ) : (
        <Center p="8">
          <RepeatClockIcon w="6" h="6" />
        </Center>
      )}
      <Box flex={1} px="4" py="2">
        <Flex
          direction="row"
          alignItems="space-between"
          justifyContent="space-between"
        >
          <Text pb={1} as="b">
            Request #{request.id}{" "}
            {request.denialReason ? (
              <Text color="red" fontSize="sm" display="inline">
                - (denied)
              </Text>
            ) : (
              ""
            )}
            {!!request.readingOrder && !request.denialReason && (
              <Text color="green.400" fontSize="sm" display="inline">
                - completed
              </Text>
            )}
          </Text>
          {!request.completed && (
            <Text fontSize="sm">
              {request.daysLeft > 0
                ? "Late"
                : Math.abs(request.daysLeft) + " days "}{" "}
              left
            </Text>
          )}
        </Flex>
        <Text pb={1} as="i">
          {request.description}
        </Text>
        <Text color="gray.400" fontSize="sm">
          Submitted on {request.submittedAt}
        </Text>
      </Box>
    </Flex>
    {!!request.denialReason && (
      <Box px={6} pb={6}>
        <Divider mt="0" mb="6" />
        <Text fontWeight="bold" color="red.500" fontSize="md">
          Redwing Team:
        </Text>
        <Text fontStyle="italic" display="inline">
          {request.denialReason}
        </Text>
      </Box>
    )}
  </Box>
);

const Requests = () => {
  const [description, setDescription] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const { isPremium } = usePremium();
  const mutation = useMutation((variables) => {
    return storeRequest(variables.description);
  });
  const requestQuery = useQuery("requests", () => getRequests(), {
    onSuccess: (data) => {
      const pending = data?.data?.find((req) => !req.completed);
      if (pending) {
        setPendingRequest(pending);
      }
    },
  });

  const onPressSubmit = () => {
    console.log("Called here", description);
    mutation
      .mutateAsync({ description })
      .then(() => {
        trackEvent("reading_order_request_submitted", {
          description_length: description.length,
        });
        requestQuery.refetch();
      })
      .catch((err) => {
        console.error("err.data.message");
      });
  };

  if (requestQuery.isLoading) {
    return (
      <Center height="80vh">
        <Spinner color="red" size="lg" />
      </Center>
    );
  }
  return (
    <Container>
      <Heading as="h2" my="6">
        My Requests
      </Heading>

      {!isPremium && (
        <Alert status="info">
          <AlertIcon />
          <Box>
            <AlertTitle>Unlock requests</AlertTitle>
            <AlertDescription>
              Upgrade to Premium to request new reading order on Redwing and get
              them added within 28 days.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {!pendingRequest ? (
        <div>
          <Text fontSize="sm" color="gray.400" mt="6" mb="2">
            REQUEST A READING ORDER
          </Text>
          <Text>
            What reading order would you like to get? Please stick to{" "}
            <u>one reading order per request</u> to help our content team.
            Redwing reserves the right to decline requests that fall outside our
            team's expertise.
          </Text>
          <Box py="2">
            <Textarea
              onChange={(e) => setDescription(e.target.value)}
              autoFocus
              placeholder="E.g: I would like to have Jason Aaron's Thor"
              disabled={!isPremium}
            />
          </Box>
          <Button
            tabIndex={3}
            onClick={() => onPressSubmit()}
            colorScheme="red"
            color="white"
            size="lg"
            fontSize="md"
            disabled={!isPremium || mutation.isLoading}
          >
            Submit request
          </Button>
          <Divider my="4" />
        </div>
      ) : (
        <>
          <Text fontSize="sm" color="gray.400" mt="6" mb="2">
            PENDING REQUEST
          </Text>
          <RequestCard request={pendingRequest} />
          <Text pt="4">
            You will be notified when your reading order is ready.
          </Text>
        </>
      )}

      <Text fontSize="sm" color="gray.400" mt="6" mb="2">
        COMPLETED REQUESTS
      </Text>
      {requestQuery.data?.data?.filter((req) => req && req.completed).length ===
        0 && <Text>You have no completed request yet.</Text>}
      {requestQuery.data?.data
        ?.filter((req) => req && req.completed)
        .map((request) => (
          <RequestCard key={request.id} request={request} />
        ))}
    </Container>
  );
};

export default Requests;
